import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useSiteMetadata } from 'helpers';

/**
 * SEO component for managing meta tags across all sites
 * Dynamically uses site configuration from API
 *
 * @param {Object} props
 * @param {string} props.title - Page-specific title (will be appended to site name)
 * @param {string} props.description - Page-specific description
 * @param {string} props.canonical - Canonical URL for this page
 * @param {string} props.robots - Robots meta tag value (default: "index, follow")
 * @param {boolean} props.noindex - Quick way to set noindex
 * @param {string} props.ogImage - Open Graph image URL
 * @param {string} props.ogType - Open Graph type (default: "website")
 */
const SEO = ({
  title,
  description,
  canonical,
  robots = 'index, follow',
  noindex = false,
  ogImage,
  ogType = 'website',
}) => {
  const siteMetadata = useSiteMetadata();

  // Build full title: "Page Title - Site Name" or just "Site Name"
  const fullTitle = title
    ? `${title} - ${siteMetadata.siteName}`
    : siteMetadata.fullSiteTitle;

  // Use provided description or fall back to site description
  const metaDescription = description || siteMetadata.enhancedDescription;

  // Build canonical URL
  const canonicalUrl = canonical || siteMetadata.canonicalBase;

  // Override robots if noindex is true
  const robotsValue = noindex ? 'noindex, follow' : robots;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta content={metaDescription} name="description" />
      <meta content={siteMetadata.keywords} name="keywords" />
      <link href={canonicalUrl} rel="canonical" />
      <meta content={robotsValue} name="robots" />

      {/* iOS "Add to Home Screen" title - uses siteName only */}
      {siteMetadata.siteName ? (
        <meta
          content={siteMetadata.siteName}
          name="apple-mobile-web-app-title"
        />
      ) : null}
      <meta content="yes" name="apple-mobile-web-app-capable" />

      {/* Open Graph / Facebook */}
      <meta content={ogType} property="og:type" />
      <meta content={canonicalUrl} property="og:url" />
      <meta content={fullTitle} property="og:title" />
      <meta content={metaDescription} property="og:description" />
      <meta content={siteMetadata.siteName} property="og:site_name" />
      {ogImage ? <meta content={ogImage} property="og:image" /> : null}

      {/* Twitter */}
      <meta content="summary_large_image" name="twitter:card" />
      <meta content={canonicalUrl} name="twitter:url" />
      <meta content={fullTitle} name="twitter:title" />
      <meta content={metaDescription} name="twitter:description" />
      {ogImage ? <meta content={ogImage} name="twitter:image" /> : null}
    </Helmet>
  );
};

export default SEO;
